import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Facebook, Megaphone } from "lucide-react";
import { API } from "@/lib/api";

const FB_PAGE_URL = "https://www.facebook.com/midwesternspeedway";

function NewsCard({ post, index }) {
  const inner = (
    <>
      <div className="relative overflow-hidden aspect-[16/10] bg-clay-surface">
        {post.image ? (
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Megaphone className="w-10 h-10 text-clay-orange/40" />
          </div>
        )}
        {(post.tag || post.source === "facebook") && (
          <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.2em] bg-black/70 backdrop-blur-sm text-clay-text px-2.5 py-1">
            {post.tag || "Facebook"}
          </span>
        )}
      </div>
      <div className="p-5 sm:p-6 flex flex-col flex-1">
        <p className="font-mono text-xs tracking-[0.15em] text-clay-orange mb-2.5">
          {new Date(post.created_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase()}
        </p>
        <h3 className="font-display font-extrabold uppercase text-xl sm:text-2xl tracking-tight leading-tight mb-3 group-hover:text-clay-orange transition-colors duration-300">
          {post.title}
        </h3>
        <p className="text-clay-muted text-sm leading-relaxed line-clamp-3 mb-5">{post.body}</p>
        <span className="mt-auto font-mono text-[11px] uppercase tracking-[0.25em] text-clay-text flex items-center gap-2 group-hover:gap-3.5 transition-all duration-300">
          Read More <ArrowRight className="w-4 h-4 text-clay-orange" />
        </span>
      </div>
    </>
  );

  const cls =
    "group flex flex-col border border-clay-border bg-clay-card/40 hover:bg-clay-card/70 hover:border-clay-orange/50 transition-colors duration-300 h-full";

  return (
    <motion.div
      data-testid="news-post"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ delay: index * 0.07 }}
      className="min-w-[82%] sm:min-w-[46%] lg:min-w-0 snap-start"
    >
      {post.link ? (
        <a href={post.link} target="_blank" rel="noopener noreferrer" className={cls}>
          {inner}
        </a>
      ) : (
        <article className={cls}>{inner}</article>
      )}
    </motion.div>
  );
}

export default function News() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetch(`${API}/news`)
      .then((r) => r.json())
      .then((d) => setPosts(Array.isArray(d) ? d : []))
      .catch(() => setPosts([]));
  }, []);

  return (
    <section id="news" data-testid="news-section" className="border-t border-clay-border">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-mono text-xs uppercase tracking-[0.35em] text-clay-orange mb-4 flex items-center gap-3"
            >
              <span className="inline-block w-10 h-0.5 bg-clay-orange" /> From the Pits
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none"
            >
              Latest <span className="text-clay-orange">News</span>
            </motion.h2>
          </div>
          <a
            data-testid="news-facebook-link"
            href={FB_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-clay-muted hover:text-clay-orange transition-colors"
          >
            <Facebook className="w-4 h-4" /> More on Facebook
          </a>
        </div>

        {posts.length === 0 ? (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted border border-dashed border-clay-border py-12 text-center">
            No club announcements yet — catch the latest on Facebook
          </p>
        ) : (
          <div className="flex lg:grid lg:grid-cols-3 gap-5 overflow-x-auto lg:overflow-visible snap-x snap-mandatory pb-4 lg:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0">
            {posts.map((p, i) => (
              <NewsCard key={p.id} post={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
